/**
 * Contrato defensivo do Response Design System.
 *
 * O backend já normaliza este objeto, mas a interface valida novamente antes
 * de renderizar. Assim, um payload parcial ou antigo nunca quebra uma conversa
 * e nenhum HTML/controlador visual vindo do modelo chega ao DOM.
 */

export const RESPONSE_MODES = Object.freeze([
  'conversation', 'analysis', 'report', 'plan', 'decision', 'reminder',
  'comparison', 'recipe', 'finance', 'study', 'task', 'calendar', 'structured',
]);

export const INLINE_COLORS = Object.freeze(['blue', 'lightBlue', 'yellow', 'rose', 'green']);

const BLOCK_TYPES = new Set(['heading', 'paragraph', 'list', 'quote', 'divider', 'meta']);
const INLINE_TYPES = new Set(['text', 'bold', 'highlight', 'colored', 'link']);
const MAX_TEXT = 1800;
const MAX_BLOCKS = 24;
const MAX_ITEMS = 16;
const ISO_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,3})?)?(?:Z|[+-](?:0\d|1\d|2[0-3]):?[0-5]\d)$/;

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function safeText(value, max = MAX_TEXT) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .trim()
    .slice(0, max);
}

// Espaços entre segmentos são semânticos: o modelo pode separar
// "melhorar a " e "presença percebida" para destacar só a segunda parte.
function safeInlineText(value, max = MAX_TEXT) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .slice(0, max);
}

function safeUrl(value) {
  if (typeof value !== 'string' || value.length > 2048) return '';
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
  } catch {
    return '';
  }
}

function safeTimestamp(value) {
  if (typeof value !== 'string' || value.length > 64) return '';
  const match = value.match(ISO_TIMESTAMP);
  if (!match) return '';

  const [, year, month, day] = match;
  const numericYear = Number(year);
  const numericMonth = Number(month);
  const numericDay = Number(day);
  const lastDayOfMonth = new Date(Date.UTC(numericYear, numericMonth, 0)).getUTCDate();
  if (numericMonth < 1 || numericMonth > 12 || numericDay < 1 || numericDay > lastDayOfMonth) return '';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

function safeTimeZone(value) {
  const timeZone = safeText(value, 100);
  if (!timeZone || !/^(?:UTC|[A-Za-z][A-Za-z0-9_.+-]*(?:\/[A-Za-z0-9_.+-]+)+)$/.test(timeZone)) return '';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function normalizeUtility(value, mode) {
  if (mode !== 'conversation' || !isRecord(value) || value.kind !== 'clock') return null;
  const at = safeTimestamp(value.at);
  const timeZone = safeTimeZone(value.timeZone);
  if (!at || !timeZone) return null;
  const label = safeText(value.label, 80);
  return {
    kind: 'clock',
    at,
    timeZone,
    ...(label ? { label } : {}),
  };
}

function applyInlineEmphasisBudget(content, budget) {
  return content.map(segment => {
    if (segment.type === 'highlight') {
      budget.highlights += 1;
      return budget.highlights <= 2 ? segment : { type: 'bold', text: segment.text };
    }
    if (segment.type === 'colored') {
      budget.colored += 1;
      return budget.colored <= 5 ? segment : { type: 'bold', text: segment.text };
    }
    return segment;
  });
}

function applyBlockEmphasisBudget(block, budget) {
  if (block.type === 'list') {
    return {
      ...block,
      items: block.items.map(item => ({
        ...item,
        content: applyInlineEmphasisBudget(item.content, budget),
      })),
    };
  }
  if (!block.content) return block;
  return { ...block, content: applyInlineEmphasisBudget(block.content, budget) };
}

// Long conversational answers stay scannable when emphasis is deliberate.
// Structured cards retain their full semantic detail because their visual
// hierarchy is already concentrated in a smaller surface.
function applyConversationEmphasisBudget(message, sections) {
  const budget = { highlights: 0, colored: 0 };
  return {
    message: message.map(block => applyBlockEmphasisBudget(block, budget)),
    sections: sections.map(section => ({
      ...section,
      blocks: section.blocks.map(block => applyBlockEmphasisBudget(block, budget)),
    })),
  };
}

export function normalizeInline(value) {
  if (typeof value === 'string') {
    const text = safeInlineText(value);
    return text.trim() ? { type: 'text', text } : null;
  }
  if (!isRecord(value) || !INLINE_TYPES.has(value.type)) return null;

  const text = safeInlineText(value.text);
  if (!text.trim()) return null;
  const type = value.type;
  const color = INLINE_COLORS.includes(value.color) ? value.color : undefined;

  if (type === 'link') {
    const href = safeUrl(value.href);
    return href ? { type, text, href } : { type: 'text', text };
  }
  if (type === 'highlight' || type === 'colored') return { type, text, ...(color ? { color } : {}) };
  return { type, text };
}

export function normalizeInlineList(value) {
  const source = Array.isArray(value) ? value : typeof value === 'string' ? [value] : [];
  return source.map(normalizeInline).filter(Boolean).slice(0, 60);
}

function normalizeListItem(value) {
  if (typeof value === 'string') {
    const content = normalizeInlineList(value);
    return content.length ? { content } : null;
  }
  if (!isRecord(value)) return null;
  const content = normalizeInlineList(value.content || value.text);
  if (!content.length) return null;
  return { content, checked: value.checked === true };
}

export function normalizeBlock(value) {
  if (!isRecord(value) || !BLOCK_TYPES.has(value.type)) return null;
  const type = value.type;
  if (type === 'divider') return { type };

  if (type === 'list') {
    const items = (Array.isArray(value.items) ? value.items : [])
      .map(normalizeListItem)
      .filter(Boolean)
      .slice(0, MAX_ITEMS);
    if (!items.length) return null;
    return {
      type,
      style: ['bullet', 'ordered', 'check'].includes(value.style) ? value.style : 'bullet',
      items,
    };
  }

  if (type === 'meta') {
    const label = safeText(value.label, 100);
    const content = normalizeInlineList(value.content || value.text);
    return label && content.length ? { type, label, content } : null;
  }

  const content = normalizeInlineList(value.content || value.text);
  if (!content.length) return null;
  return {
    type,
    content,
    ...(type === 'heading' ? { level: [1, 2, 3].includes(value.level) ? value.level : 2 } : {}),
  };
}

export function normalizeSections(value) {
  if (!Array.isArray(value)) return [];
  return value.map((section, index) => {
    if (!isRecord(section)) return null;
    const blocks = (Array.isArray(section.blocks) ? section.blocks : [])
      .map(normalizeBlock)
      .filter(Boolean)
      .slice(0, MAX_BLOCKS);
    const title = safeText(section.title, 180);
    if (!title && !blocks.length) return null;
    return {
      id: safeText(section.id, 80) || `section-${index + 1}`,
      title,
      icon: safeText(section.icon, 40),
      blocks,
    };
  }).filter(Boolean).slice(0, 12);
}

function normalizeCardItem(value) {
  if (typeof value === 'string') {
    const text = safeText(value, 280);
    return text ? { text } : null;
  }
  if (!isRecord(value)) return null;
  const text = safeText(value.text || value.title, 280);
  if (!text) return null;
  return {
    text,
    detail: safeText(value.detail, 420),
    status: safeText(value.status, 80),
    checked: value.checked === true,
  };
}

function normalizeChoice(value) {
  if (!isRecord(value)) return null;
  const title = safeText(value.title, 160);
  const points = (Array.isArray(value.points) ? value.points : [])
    .map(point => safeText(point, 220))
    .filter(Boolean)
    .slice(0, 8);
  return title && points.length ? { title, points } : null;
}

function normalizeCard(value) {
  if (!isRecord(value)) return null;
  const title = safeText(value.title, 180);
  const summary = safeText(value.summary, 500);
  const items = (Array.isArray(value.items) ? value.items : [])
    .map(normalizeCardItem)
    .filter(Boolean)
    .slice(0, MAX_ITEMS);
  const left = normalizeChoice(value.comparison?.left || value.left);
  const right = normalizeChoice(value.comparison?.right || value.right);
  const actions = (Array.isArray(value.actions) ? value.actions : [])
    .map(action => isRecord(action) ? {
      id: safeText(action.id, 64),
      label: safeText(action.label, 80),
      tone: ['primary', 'quiet'].includes(action.tone) ? action.tone : 'quiet',
    } : null)
    .filter(action => action?.id && action.label)
    .slice(0, 2);

  if (!title && !summary && !items.length && !left && !right) return null;
  return {
    ...(title ? { title } : {}),
    ...(summary ? { summary } : {}),
    ...(safeText(value.eyebrow, 80) ? { eyebrow: safeText(value.eyebrow, 80) } : {}),
    ...(safeText(value.when, 100) ? { when: safeText(value.when, 100) } : {}),
    ...(items.length ? { items } : {}),
    ...(left && right ? { comparison: { left, right } } : {}),
    ...(actions.length ? { actions } : {}),
  };
}

/**
 * Returns a safe presentation or null. `null` deliberately means: render the
 * existing Markdown/text path untouched.
 */
export function normalizeResponsePresentation(raw) {
  if (!isRecord(raw) || raw.version !== 1 || !RESPONSE_MODES.includes(raw.mode)) return null;
  const rawMessage = (Array.isArray(raw.message) ? raw.message : [])
    .map(normalizeBlock)
    .filter(Boolean)
    .slice(0, MAX_BLOCKS);
  const rawSections = normalizeSections(raw.sections);
  const fullContent = normalizeSections(raw.full_content || raw.fullContent);
  const card = normalizeCard(raw.card);
  const title = safeText(raw.title, 180);
  const summary = safeText(raw.summary, 500);
  const utility = normalizeUtility(raw.utility, raw.mode);
  const { message, sections } = raw.mode === 'conversation'
    ? applyConversationEmphasisBudget(rawMessage, rawSections)
    : { message: rawMessage, sections: rawSections };

  // A semantic envelope without actual readable content should be ignored.
  if (!title && !summary && !message.length && !sections.length && !fullContent.length && !card && !utility) return null;

  return {
    version: 1,
    mode: raw.mode,
    ...(title ? { title } : {}),
    ...(summary ? { summary } : {}),
    ...(message.length ? { message } : {}),
    ...(sections.length ? { sections } : {}),
    ...(card ? { card } : {}),
    ...(utility ? { utility } : {}),
    // `full_content` is semantic evidence of expandable detail. Do not lose
    // valid material merely because an optional boolean was omitted upstream.
    expandable: fullContent.length > 0,
    ...(fullContent.length ? { fullContent } : {}),
  };
}

export function isStructuredMode(mode) {
  return mode && mode !== 'conversation';
}
